import { Workorder, GeocodeResult } from '@certhon/models';
import { LatLngTuple } from 'leaflet';
import { resolveUrl, verifyResponse } from '@/lib/api-utils';
import { unique } from 'remeda';
import { formatAddressForGeocoding } from './shared';

export interface WorkorderCoordinate {
  recnum: number;
  position: LatLngTuple;
}

/** Maximum addresses per geocode request (API limit) */
const GEOCODE_BATCH_SIZE = 100;

/**
 * Geocode multiple addresses using the dedicated geocoding endpoint
 * Much more efficient than the routing endpoint - uses automatic caching
 * Automatically batches requests to respect the 100 address API limit
 */
async function geocodeAddresses(addresses: string[]): Promise<GeocodeResult[]> {
  if (addresses.length === 0) return [];

  // Batch addresses into chunks of GEOCODE_BATCH_SIZE
  const batches: string[][] = [];
  for (let i = 0; i < addresses.length; i += GEOCODE_BATCH_SIZE) {
    batches.push(addresses.slice(i, i + GEOCODE_BATCH_SIZE));
  }

  // Process all batches in parallel
  const batchResults = await Promise.all(
    batches.map(async (batch) => {
      const response = await fetch(resolveUrl('/routing/geocode'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ addresses: batch }),
      });

      await verifyResponse(response);
      return response.json() as Promise<GeocodeResult[]>;
    }),
  );

  // Flatten results back into a single array
  return batchResults.flat();
}

/**
 * Geocode workorder addresses using the dedicated geocoding API
 * Returns a map of workorder recnum to coordinates
 * Automatically handles caching and batch processing on the backend
 */
export async function geocodeWorkorderAddresses(
  workorders: Workorder[],
): Promise<Map<number, LatLngTuple>> {
  // Filter workorders with addresses
  const workordersWithAddress = workorders.filter((wo) => wo.location_address);

  if (workordersWithAddress.length === 0) {
    return new Map();
  }

  // Format addresses for geocoding
  const addresses = workordersWithAddress.map((wo) =>
    formatAddressForGeocoding(wo.location_address!),
  );

  // Geocode all addresses in a single request (backend handles caching/batching)
  const results = await geocodeAddresses(unique(addresses));

  // Map results back to workorders
  const coordinatesMap = new Map<number, LatLngTuple>();

  workordersWithAddress.forEach((wo, idx) => {
    const result = results[idx];
    if (result?.coordinates) {
      // Geocode result is [lng, lat], Leaflet needs [lat, lng]
      coordinatesMap.set(wo.recnum, [
        result.coordinates[1],
        result.coordinates[0],
      ]);
    }
  });

  return coordinatesMap;
}
